import {
  BearingEnvelope,
  Level,
  Point2D,
  RoofPlane,
  TopOrBottomCut,
  TrussEnvelope,
  TrussType,
} from '@paragon/contract-library';
import { Line2D, slopeToDegrees } from '@paragon/geometry-library';

import {
  calculateTrussEnvelopeCapSetGroupings,
  CapSetState,
} from '../../library/layout-geometry/truss-envelopes/capSets';
import { INITIAL_TRUSS_ENVELOPE_PROPERTIES } from '../../library/layout-geometry/truss-envelopes/defaults';
import { getBearingEnvelopeSelectableType } from '../../library/selection/types';
import { findNextName } from '../../library/utilities/findNextName';
import type { RootState } from '../../store';
import { deleteComponentDesignsWithoutConfirmation, deleteItems } from '../../store/actions/deletionActions';
import { editBearingEnvelopes } from '../../store/actions/layout/bearingEnvelopeActions';
import { autoCutPlanes, editRoofPlanes } from '../../store/actions/layout/roofPlaneActions';
import {
  createTrussEnvelopes,
  designSelectedTrussEnvelopes,
  editCapSet,
  editExistingTrussEnvelopes,
  recalculateExistingTrussEnvelopes,
  trimOrExtendEnvelopes,
} from '../../store/actions/layout/trussEnvelopeActions';
import { selectItemsByGuid } from '../../store/actions/selectActions';
import { getActiveLevelOrNull, getBearingEnvelopeElevationsFromLevel } from '../../store/selectors/layoutDataSelectors';
import {
  DEFAULT_BEAM_DEPTH,
  DEFAULT_BEARING_ENVELOPE_SETTINGS,
  DEFAULT_WALL_DEPTH,
} from '../../store/slices/applicationSlice';
import { describeLayout, describeRoofPlane, findLevelByName, shortId } from './contextTools';
import { findTrussByName, requireProject, requireString, requireStringArray, waitForAppToSettle } from './helpers';
import { describeTrussEnvelope } from './layoutDescriptions';
import { defineTool, ToolError } from './types';

const POINT_SCHEMA = {
  type: 'object',
  properties: { x: { type: 'number' }, y: { type: 'number' } },
  required: ['x', 'y'],
  description: 'Inches in the level’s plan coordinates.',
};

const LEVEL_SCHEMA = { type: 'string', description: 'The level’s name. Defaults to the active level.' };

function requirePoint(value: unknown, label: string): Point2D {
  const point = value as Partial<Point2D> | null;
  if (typeof point?.x !== 'number' || typeof point?.y !== 'number')
    throw new ToolError(`"${label}" must be a point with numeric x and y, in inches.`);
  return { x: point.x, y: point.y };
}

function resolveLevel(state: RootState, levelName: string | undefined) {
  return levelName != null ? findLevelByName(state, levelName) : getActiveLevelOrNull(state);
}

function findBearingEnvelopeByName(state: RootState, name: string): BearingEnvelope {
  const matches = Object.values(state.layout.bearingEnvelopes).filter(
    (envelope) => envelope.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (matches.length === 0) throw new ToolError(`There is no wall or beam named "${name}".`);
  if (matches.length > 1)
    throw new ToolError(`More than one wall or beam is named "${name}". Ask the designer which one they mean.`);
  return matches[0];
}

function findTrussEnvelopeByName(state: RootState, name: string): TrussEnvelope {
  const matches = Object.values(state.layout.trussEnvelopes).filter(
    (envelope) => envelope.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (matches.length === 0) throw new ToolError(`There is no truss envelope named "${name}".`);
  if (matches.length > 1) throw new ToolError(`More than one truss envelope is named "${name}".`);
  return matches[0];
}

function findRoofPlaneById(state: RootState, id: string): RoofPlane {
  const matches = Object.values(state.layout.roofPlanes).filter((plane) => plane.guid.startsWith(id));
  if (matches.length !== 1) throw new ToolError(`There is no roof plane "${id}". Use the ids get_layout returns.`);
  return matches[0];
}

/**
 * Converts a roof pitch given as rise per 12 to the slope the app stores. A pitch of 90 or more means a vertical
 * gable end.
 */
export function pitchToSlopeInDegrees(pitch: number) {
  return pitch >= 90 ? 90 : slopeToDegrees(pitch);
}

interface WallInput {
  readonly from: unknown;
  readonly to: unknown;
  readonly type?: 'Wall' | 'Beam';
  readonly thickness?: number;
  readonly name?: string;
}

export function buildBearingEnvelopes(
  existingEnvelopes: readonly BearingEnvelope[],
  walls: readonly WallInput[],
  level: Pick<Level, 'guid' | 'plateElevation' | 'wallHeight'> | null,
): BearingEnvelope[] {
  const takenNames = existingEnvelopes.map((envelope) => envelope.name);

  return walls.map((wall, index) => {
    const bearingType = wall.type ?? 'Wall';
    const name = wall.name ?? findNextName(takenNames);
    takenNames.push(name);

    const depth = bearingType === 'Beam' ? DEFAULT_BEAM_DEPTH : DEFAULT_WALL_DEPTH;
    const properties = { ...DEFAULT_BEARING_ENVELOPE_SETTINGS, bearingType, ...depth };
    const elevations = level == null ? {} : getBearingEnvelopeElevationsFromLevel(level, properties);

    const leftPoint = requirePoint(wall.from, `walls[${index}].from`);
    const rightPoint = requirePoint(wall.to, `walls[${index}].to`);
    if (leftPoint.x === rightPoint.x && leftPoint.y === rightPoint.y)
      throw new ToolError(`walls[${index}] starts and ends at the same point.`);

    return {
      ...properties,
      ...elevations,
      guid: crypto.randomUUID(),
      name,
      leftPoint,
      rightPoint,
      thickness: wall.thickness ?? properties.thickness,
      levelGuid: level?.guid ?? null,
    };
  });
}

export const layoutTools = [
  defineTool<{ walls: WallInput[]; level?: string }>({
    name: 'add_walls',
    description:
      'Adds bearing walls or beams to a level. Give each one’s ends in inches in the level’s plan coordinates. For a ' +
      'building outline, go around it counterclockwise so the roof planes face outward. Returns the names given.',
    inputSchema: {
      type: 'object',
      properties: {
        walls: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              from: POINT_SCHEMA,
              to: POINT_SCHEMA,
              type: { type: 'string', enum: ['Wall', 'Beam'], description: 'Defaults to Wall.' },
              thickness: { type: 'number', description: 'Inches. Defaults to 5.5.' },
              name: { type: 'string' },
            },
            required: ['from', 'to'],
          },
        },
        level: LEVEL_SCHEMA,
      },
      required: ['walls'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      if (!Array.isArray(input.walls) || input.walls.length === 0) throw new ToolError('Give at least one wall.');

      const envelopes = buildBearingEnvelopes(
        Object.values(getState().layout.bearingEnvelopes),
        input.walls,
        resolveLevel(getState(), input.level),
      );
      await dispatch(
        editBearingEnvelopes(
          envelopes.map(({ guid }) => ({ guid, editedProperties: {} })),
          { createdEnvelopes: envelopes },
        ),
      );
      await waitForAppToSettle(getState);
      return { summary: { added: envelopes.map(({ name, bearingType }) => ({ name, type: bearingType })) } };
    },
  }),

  defineTool<{ name: string; from?: unknown; to?: unknown; thickness?: number; top?: number; bottom?: number }>({
    name: 'edit_wall',
    description: 'Moves or resizes a wall or beam. Elevations are in inches.',
    inputSchema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        from: POINT_SCHEMA,
        to: POINT_SCHEMA,
        thickness: { type: 'number' },
        top: { type: 'number' },
        bottom: { type: 'number' },
      },
      required: ['name'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const envelope = findBearingEnvelopeByName(getState(), input.name);
      const editedProperties: Partial<BearingEnvelope> = {
        ...(input.from != null ? { leftPoint: requirePoint(input.from, 'from') } : {}),
        ...(input.to != null ? { rightPoint: requirePoint(input.to, 'to') } : {}),
        ...(input.thickness != null ? { thickness: input.thickness } : {}),
        ...(input.top != null ? { top: input.top } : {}),
        ...(input.bottom != null ? { bottom: input.bottom } : {}),
      };
      await dispatch(editBearingEnvelopes({ guid: envelope.guid, editedProperties }));
      await waitForAppToSettle(getState);

      const edited = getState().layout.bearingEnvelopes[envelope.guid];
      return { summary: { name: edited.name, from: edited.leftPoint, to: edited.rightPoint, top: edited.top } };
    },
  }),

  defineTool<{ names: string[] }>({
    name: 'delete_walls',
    description:
      'Deletes walls or beams by name. Roof planes added on them stay, since a plane isn’t tied to its wall; delete ' +
      'those with delete_roof_planes.',
    inputSchema: {
      type: 'object',
      properties: { names: { type: 'array', items: { type: 'string' } } },
      required: ['names'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const envelopes = requireStringArray(input, 'names').map((name) => findBearingEnvelopeByName(getState(), name));
      for (const type of ['wall', 'beam'] as const) {
        const items = envelopes
          .filter((envelope) => getBearingEnvelopeSelectableType(envelope) === type)
          .map((envelope) => ({ type, guid: envelope.guid }));
        if (items.length > 0) {
          await dispatch(deleteItems(items));
          await waitForAppToSettle(getState);
        }
      }

      const remaining = envelopes.filter((envelope) => getState().layout.bearingEnvelopes[envelope.guid] != null);
      if (remaining.length > 0)
        throw new ToolError(`Could not delete ${remaining.map((envelope) => envelope.name).join(', ')}.`);
      return { summary: { deleted: envelopes.map((envelope) => envelope.name) } };
    },
  }),

  defineTool<{ ids: string[] }>({
    name: 'delete_roof_planes',
    description:
      'Deletes roof planes by the ids get_layout gives them, for example to replace a roof’s planes after its walls ' +
      'change. Cut the remaining planes again with auto_cut_roof_planes, and re-fit truss envelopes on them.',
    inputSchema: {
      type: 'object',
      properties: { ids: { type: 'array', items: { type: 'string' } } },
      required: ['ids'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const planes = requireStringArray(input, 'ids').map((id) => findRoofPlaneById(getState(), id));
      if (planes.length === 0) throw new ToolError('Name at least one roof plane.');
      await dispatch(deleteItems(planes.map((plane) => ({ type: 'roofPlane' as const, guid: plane.guid }))));
      await waitForAppToSettle(getState);

      const remaining = planes.filter((plane) => getState().layout.roofPlanes[plane.guid] != null);
      if (remaining.length > 0)
        throw new ToolError(`Could not delete ${remaining.map((plane) => shortId(plane.guid)).join(', ')}.`);
      return { summary: { deleted: planes.map((plane) => shortId(plane.guid)) } };
    },
  }),

  defineTool<{ walls: string[]; pitch?: number; overhang?: number; autoCut?: boolean }>({
    name: 'add_roof_planes',
    description:
      'Adds a roof plane on each named wall, sloping up from it, then cuts every plane on the level against each ' +
      'other as the app’s Auto Cut does. Pitch is rise per 12 (6 means 6/12); 90 makes a vertical gable end. ' +
      'Overhang is in inches. Every exterior wall needs a plane: for a gable roof, sloped planes on the eave walls and ' +
      'planes with pitch 90 on the gable-end walls; for a hip roof, sloped planes on every wall. Cuts set by hand ' +
      'with set_roof_plane_cuts need setting again after it.',
    inputSchema: {
      type: 'object',
      properties: {
        walls: { type: 'array', items: { type: 'string' } },
        pitch: { type: 'number', description: 'Defaults to the app’s roof plane default.' },
        overhang: { type: 'number' },
        autoCut: { type: 'boolean', description: 'Defaults to true.' },
      },
      required: ['walls'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      requireProject(state);
      const defaults = state.layout.roofPlaneInsertionSettings.defaultProperties;

      const roofPlanes: RoofPlane[] = requireStringArray(input, 'walls').map((wallName) => {
        const wall = findBearingEnvelopeByName(state, wallName);
        return {
          guid: crypto.randomUUID(),
          ...defaults,
          ...(input.pitch != null ? { slopeInDegrees: pitchToSlopeInDegrees(input.pitch) } : {}),
          ...(input.overhang != null ? { overhang: input.overhang } : {}),
          flipped: false,
          geometry: { type: 'BearingEnvelope' as const, bearingEnvelopeGuid: wall.guid },
          levelGuid: wall.levelGuid,
          cuts: [],
        };
      });

      await dispatch(
        editRoofPlanes(
          roofPlanes.map(({ guid }) => ({ guid, editedProperties: {} })),
          { createdPlanes: roofPlanes },
        ),
      );
      // Cut the new planes together with the rest of their level's roof: planes added in separate calls, such as eaves
      // and then gable ends, only bound each other when they're cut together.
      if (input.autoCut ?? true) {
        const levels = new Set(roofPlanes.map((plane) => plane.levelGuid));
        const planesOnLevels = Object.values(getState().layout.roofPlanes).filter((plane) =>
          levels.has(plane.levelGuid),
        );
        await dispatch(autoCutPlanes(planesOnLevels.map((plane) => plane.guid)));
      }
      await waitForAppToSettle(getState);

      return {
        summary: {
          added: roofPlanes.map((plane) =>
            describeRoofPlane(getState(), getState().layout.roofPlanes[plane.guid] ?? plane),
          ),
        },
      };
    },
  }),

  defineTool<{ id: string; pitch?: number; overhang?: number }>({
    name: 'edit_roof_plane',
    description: 'Changes a roof plane’s pitch (rise per 12; 90 for a gable end) or overhang (inches).',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The id get_layout returns.' },
        pitch: { type: 'number' },
        overhang: { type: 'number' },
      },
      required: ['id'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const plane = findRoofPlaneById(getState(), input.id);
      await dispatch(
        editRoofPlanes({
          guid: plane.guid,
          editedProperties: {
            ...(input.pitch != null ? { slopeInDegrees: pitchToSlopeInDegrees(input.pitch) } : {}),
            ...(input.overhang != null ? { overhang: input.overhang } : {}),
          },
        }),
      );
      await waitForAppToSettle(getState);
      const edited = getState().layout.roofPlanes[plane.guid];
      return { summary: { id: shortId(plane.guid), slopeInDegrees: edited.slopeInDegrees, overhang: edited.overhang } };
    },
  }),

  defineTool<{ ids?: string[] }>({
    name: 'auto_cut_roof_planes',
    description:
      'Cuts roof planes against each other, as the app’s Auto Cut does. Defaults to every plane on the level.',
    inputSchema: { type: 'object', properties: { ids: { type: 'array', items: { type: 'string' } } } },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      requireProject(state);
      const level = getActiveLevelOrNull(state);
      const planes =
        input.ids != null
          ? input.ids.map((id) => findRoofPlaneById(state, id))
          : Object.values(state.layout.roofPlanes).filter(
              (plane) => level == null || plane.levelGuid == null || plane.levelGuid === level.guid,
            );
      await dispatch(autoCutPlanes(planes.map((plane) => plane.guid)));
      await waitForAppToSettle(getState);
      return {
        summary: {
          cut: planes.map((plane) => describeRoofPlane(getState(), getState().layout.roofPlanes[plane.guid] ?? plane)),
        },
      };
    },
  }),

  defineTool<{ id: string; from: unknown; to: unknown }>({
    name: 'cut_roof_plane',
    description: 'Cuts a roof plane along a vertical line through two points, in inches in plan coordinates.',
    inputSchema: {
      type: 'object',
      properties: { id: { type: 'string' }, from: POINT_SCHEMA, to: POINT_SCHEMA },
      required: ['id', 'from', 'to'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const plane = findRoofPlaneById(getState(), input.id);
      const segment = { basePoint: requirePoint(input.from, 'from'), endPoint: requirePoint(input.to, 'to') };
      await dispatch(
        editRoofPlanes({
          guid: plane.guid,
          editedProperties: {
            cuts: [...plane.cuts, { type: 'AbsoluteVertical', segment, isInfinite: false }],
          },
        }),
      );
      await waitForAppToSettle(getState);
      return { summary: describeRoofPlane(getState(), getState().layout.roofPlanes[plane.guid]) };
    },
  }),

  defineTool<{ id: string; againstPlanes: string[]; keepLineCuts?: boolean }>({
    name: 'set_roof_plane_cuts',
    description:
      'Sets which roof planes cut a roof plane, as the plane’s Cuts in its properties do, replacing its plane cuts. ' +
      'Auto Cut keeps the lowest plane everywhere, which is right for a simple roof but wrong where a smaller roof ' +
      'meets a bigger one, such as a bump-out, L, or T: there the smaller roof’s end plane runs under the main roof ' +
      'and takes it over. Fix each plane by naming the planes that bound it: a bump-out’s end plane by its two side ' +
      'planes (hips); each side plane by the end plane, the other side, and the main plane it meets (valley); the ' +
      'main plane by its own neighbors only (the ridge plane and the gable ends or hips), since in valley framing ' +
      'the main roof runs on under the bump-out. List them in order around the plane, starting beside one end of ' +
      'its wall: each corner is where one cut meets the next, so a main plane on the south wall is west end, ridge ' +
      'plane, east end, never ridge right after the wall. Lines added with cut_roof_plane stay unless ' +
      '`keepLineCuts` is false.',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The roof plane’s id from get_layout.' },
        againstPlanes: { type: 'array', items: { type: 'string' }, description: 'Ids of the planes that cut it.' },
        keepLineCuts: { type: 'boolean', description: 'Defaults to true.' },
      },
      required: ['id', 'againstPlanes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const plane = findRoofPlaneById(getState(), requireString(input, 'id'));
      const against = requireStringArray(input, 'againstPlanes').map((id) => findRoofPlaneById(getState(), id));
      if (against.some(({ guid }) => guid === plane.guid)) throw new ToolError('A plane can’t cut itself.');

      const lineCuts = input.keepLineCuts === false ? [] : plane.cuts.filter(({ type }) => type === 'AbsoluteVertical');
      await dispatch(
        editRoofPlanes({
          guid: plane.guid,
          editedProperties: {
            cuts: [
              ...against.map(({ guid }) => ({ type: 'AgainstPlane' as const, cuttingPlaneGuid: guid })),
              ...lineCuts,
            ],
          },
        }),
      );
      await waitForAppToSettle(getState);
      const described = describeRoofPlane(getState(), getState().layout.roofPlanes[plane.guid]);
      if ('bounded' in described)
        throw new ToolError(
          `Plane ${described.id} doesn’t close with those cuts. List them in order around the plane, so each one ` +
            'meets the next at a corner, and include every side: its ridge and both ends.',
        );
      return { summary: described };
    },
  }),

  defineTool<{
    envelopes: { from: unknown; to: unknown; type?: TrussType; plies?: number; name?: string }[];
    roof?: string;
    level?: string;
    sitOnRoofPlanes?: string[];
  }>({
    name: 'add_truss_envelopes',
    description:
      'Places truss envelopes, where trusses go, along lines in plan coordinates (inches). The app extends each to the ' +
      'roof and bearings it crosses, as when the designer draws one. Roof trusses go in a roof; give its name if the ' +
      'level has more than one. A roof truss’s thickness lies to the left of its line, looking from `from` to `to`, so ' +
      'draw an envelope on an end wall in the direction that keeps the truss inside the building: going around the ' +
      'building counterclockwise does. For valley trusses, where one roof runs into another, give ' +
      '`sitOnRoofPlanes`: the ids of the roof planes they sit on (the main roof under the valley), and draw them ' +
      'parallel to the smaller roof’s trusses, over the main roof, about 24 in apart. The app trims each to the space ' +
      'between those planes and the roof above, and design_trusses designs them as valley trusses. Returns the names ' +
      'given.',
    inputSchema: {
      type: 'object',
      properties: {
        envelopes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              from: POINT_SCHEMA,
              to: POINT_SCHEMA,
              type: { type: 'string', enum: ['RoofTruss', 'FloorTruss'], description: 'Defaults to RoofTruss.' },
              plies: { type: 'number' },
              name: { type: 'string' },
            },
            required: ['from', 'to'],
          },
        },
        roof: {
          type: 'string',
          description:
            'Only for roofs imported from a file, by the name get_layout gives them. Leave it out for roofs built ' +
            'from roof planes.',
        },
        level: LEVEL_SCHEMA,
        sitOnRoofPlanes: {
          type: 'array',
          items: { type: 'string' },
          description: 'Roof plane ids from get_layout that these envelopes sit on, for valley trusses.',
        },
      },
      required: ['envelopes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      requireProject(state);
      if (!Array.isArray(input.envelopes) || input.envelopes.length === 0)
        throw new ToolError('Give at least one truss envelope.');
      // A valley truss's bottom chord follows the roof under it: each plane it sits on cuts it from below.
      const bottomCuts: TopOrBottomCut[] = (input.sitOnRoofPlanes ?? []).map((id) => ({
        type: 'RoofPlane',
        roofPlaneGuid: findRoofPlaneById(state, id).guid,
      }));

      const level = resolveLevel(state, input.level);
      const roofContainers = Object.values(state.layout.roofContainers).filter(
        (container) => level == null || container.levelGuid === level.guid,
      );
      // Roofs built from roof planes have no container: the app computes their envelopes from the planes. Containers
      // only come from imported roofs, so use one only when the designer names it or the level has nothing else.
      const roofPlanesOnLevel = Object.values(state.layout.roofPlanes).filter(
        (plane) => level == null || plane.levelGuid == null || plane.levelGuid === level.guid,
      );
      const roofContainer =
        input.roof != null
          ? roofContainers.find((container) => container.name.trim().toLowerCase() === input.roof?.trim().toLowerCase())
          : roofPlanesOnLevel.length === 0
            ? roofContainers[0]
            : undefined;
      if (input.roof != null && roofContainer == null) throw new ToolError(`There is no roof named "${input.roof}".`);

      const takenNames = Object.values(state.layout.trussEnvelopes).map((envelope) => envelope.name);
      const envelopes: TrussEnvelope[] = input.envelopes.map((envelope, index) => {
        const componentType = envelope.type ?? 'RoofTruss';
        const definitionType = componentType === 'RoofTruss' ? 'Profile' : 'FeaturePattern';
        if (componentType === 'RoofTruss' && roofContainer == null && roofPlanesOnLevel.length === 0)
          throw new ToolError('There is no roof on this level yet. Add roof planes first.');

        const name = envelope.name ?? findNextName(takenNames);
        takenNames.push(name);

        return {
          ...state.layout.trussEnvelopeInsertionSettings.defaultProperties[definitionType],
          ...INITIAL_TRUSS_ENVELOPE_PROPERTIES[definitionType],
          ...(envelope.plies != null ? { numberOfPlies: envelope.plies } : {}),
          guid: crypto.randomUUID(),
          name,
          levelGuid: level?.guid ?? null,
          componentType,
          definitionType,
          roofContainerGuid: definitionType === 'Profile' ? (roofContainer?.guid ?? null) : null,
          featurePatternContainerGuid: null,
          leftPoint: requirePoint(envelope.from, `envelopes[${index}].from`),
          rightPoint: requirePoint(envelope.to, `envelopes[${index}].to`),
          elevation: level?.plateElevation ?? 0,
          leftOverhang: 0,
          rightOverhang: 0,
          leftBevelCut: null,
          rightBevelCut: null,
          leftEndConnectionOverride: null,
          rightEndConnectionOverride: null,
          componentDesignGuid: null,
          topCuts: [],
          bottomCuts,
          orderedTopCuts: { cuts: [], reversed: false },
          orderedBottomCuts: { cuts: [], reversed: false },
          containerLevel: 'NotCapped',
          capLevelOne: 0,
          capLevelTwo: 0,
          featurePatternSettings: null,
          labelPlacement: 'Left',
        };
      });

      const { success } = await dispatch(createTrussEnvelopes(envelopes));
      if (!success) throw new ToolError('The app could not save the truss envelopes.');
      await dispatch(recalculateExistingTrussEnvelopes(envelopes));
      await waitForAppToSettle(getState);

      return {
        summary: {
          added: envelopes.map((envelope) => {
            const saved = getState().layout.trussEnvelopes[envelope.guid];
            return {
              name: saved.name,
              from: saved.leftPoint,
              to: saved.rightPoint,
              elevation: saved.elevation,
              error: getState().layout.trussEnvelopeErrors[envelope.guid] ?? null,
            };
          }),
        },
      };
    },
  }),

  defineTool<{ envelopes?: string[] }>({
    name: 'design_trusses',
    description:
      'Generates trusses in truss envelopes, as the app’s Design button does. Defaults to every envelope with no truss ' +
      'yet. An envelope whose truss would match an existing truss gets that truss, as in the app, so a truss’s name ' +
      'can differ from its envelope’s and one truss can sit in several envelopes. Returns each envelope’s truss, ' +
      'whether it reused an existing one, and any failure with why.',
    inputSchema: {
      type: 'object',
      properties: { envelopes: { type: 'array', items: { type: 'string' }, description: 'Truss envelope names.' } },
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      requireProject(state);
      const envelopes =
        input.envelopes != null
          ? input.envelopes.map((name) => findTrussEnvelopeByName(state, name))
          : Object.values(state.layout.trussEnvelopes).filter((envelope) => envelope.componentDesignGuid == null);
      if (envelopes.length === 0) throw new ToolError('Every truss envelope already has a truss.');

      await dispatch(
        selectItemsByGuid(
          envelopes.map((envelope) => envelope.guid),
          'trussEnvelope',
          { force: true },
        ),
      );
      const trussGuidsBefore = new Set(Object.keys(state.componentDesigns.data));
      await dispatch(designSelectedTrussEnvelopes());
      await waitForAppToSettle(getState);

      const after = getState();
      const layout = describeLayout(after, null).trussEnvelopes.filter((described) =>
        envelopes.some((envelope) => envelope.name === described.name),
      );
      return {
        summary: {
          trussEnvelopes: layout.map(({ name, truss, error }) => {
            const trussGuid =
              after.layout.trussEnvelopes[envelopes.find((envelope) => envelope.name === name)!.guid]
                ?.componentDesignGuid;
            return {
              name,
              truss,
              ...(trussGuid != null ? { reusedExistingTruss: trussGuidsBefore.has(trussGuid) } : {}),
              error,
            };
          }),
        },
      };
    },
  }),

  defineTool<{ envelopes: string[]; capHeight?: number; remove?: boolean }>({
    name: 'set_piggyback',
    description:
      'Makes truss envelopes piggyback, as Edit Properties > Cap set does: each splits into a flat-topped base ' +
      'envelope and a cap envelope above it (named like E1-Cap1), for roofs too tall to ship as one truss. ' +
      '`capHeight` is inches above the base envelope’s bottom, where the base trusses stop and the caps start. ' +
      '`remove` puts them back to one truss each. Run design_trusses afterward to design the base and cap trusses.',
    inputSchema: {
      type: 'object',
      properties: {
        envelopes: { type: 'array', items: { type: 'string' }, description: 'Truss envelope names.' },
        capHeight: { type: 'number', description: 'Inches above the base’s bottom, such as 96 for 8 ft.' },
        remove: { type: 'boolean', description: 'Defaults to false.' },
      },
      required: ['envelopes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const envelopes = requireStringArray(input, 'envelopes').map((name) => findTrussEnvelopeByName(getState(), name));
      if (envelopes.length === 0) throw new ToolError('Name at least one truss envelope.');
      if (input.remove !== true && (typeof input.capHeight !== 'number' || input.capHeight <= 0))
        throw new ToolError('Give `capHeight`, in inches above the base, or `remove`.');

      // The cap set's base is where the envelope's truss bears; the app works it out per group.
      const groups = calculateTrussEnvelopeCapSetGroupings(getState());
      const baseElevations = envelopes.map(({ guid }) => groups[guid]?.capSetState.baseElevation);
      if (baseElevations.some((elevation) => elevation == null))
        throw new ToolError('The app can’t work out where those envelopes bear. Check them with get_layout.');
      const before = new Set(Object.keys(getState().layout.trussEnvelopes));

      // Envelopes bearing at different elevations each get their own cap set.
      for (const elevation of new Set(baseElevations)) {
        const capSetState: CapSetState = {
          baseElevation: elevation!,
          generateBase: true,
          additionalLevels: input.remove === true ? [] : [{ elevation: input.capHeight!, generate: true }],
        };
        const current = getState().layout.trussEnvelopes;
        await dispatch(
          editCapSet(
            envelopes
              .filter((_, index) => baseElevations[index] === elevation)
              .map(({ guid }) => current[guid])
              .filter((envelope) => envelope != null),
            capSetState,
          ),
        );
      }
      await waitForAppToSettle(getState);

      // The app replaces the envelopes, so report the ones it made.
      const created = Object.values(getState().layout.trussEnvelopes).filter(({ guid }) => !before.has(guid));
      if (created.length === 0) throw new ToolError('The app made no envelopes. Check its notifications for why.');
      return { summary: { envelopes: created.map((envelope) => describeTrussEnvelope(getState(), envelope)) } };
    },
  }),

  defineTool<{
    envelopes: string[];
    from?: unknown;
    to?: unknown;
    leftOverhang?: number;
    rightOverhang?: number;
    plies?: number;
    type?: TrussType;
    recalculate?: boolean;
  }>({
    name: 'edit_truss_envelopes',
    description:
      'Edits placed truss envelopes, as their Edit Properties panel does: move their ends (plan inches), set ' +
      'overhangs (inches) or plies, or change roof/floor type. By default the app then re-fits them to the roof and ' +
      'bearings. Trusses already designed in them keep their design until regenerated with design_trusses.',
    inputSchema: {
      type: 'object',
      properties: {
        envelopes: { type: 'array', items: { type: 'string' }, description: 'Truss envelope names.' },
        from: POINT_SCHEMA,
        to: POINT_SCHEMA,
        leftOverhang: { type: 'number' },
        rightOverhang: { type: 'number' },
        plies: { type: 'number' },
        type: { type: 'string', enum: ['RoofTruss', 'FloorTruss'] },
        recalculate: { type: 'boolean', description: 'Defaults to true.' },
      },
      required: ['envelopes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const envelopes = requireStringArray(input, 'envelopes').map((name) => findTrussEnvelopeByName(getState(), name));
      if ((input.from != null || input.to != null) && envelopes.length > 1)
        throw new ToolError('Move one envelope at a time.');
      if (input.plies != null && (!Number.isInteger(input.plies) || input.plies < 1))
        throw new ToolError('Plies must be 1 or more.');

      const editedProperties: Partial<TrussEnvelope> = {
        ...(input.from != null ? { leftPoint: requirePoint(input.from, 'from') } : {}),
        ...(input.to != null ? { rightPoint: requirePoint(input.to, 'to') } : {}),
        ...(input.leftOverhang != null ? { leftOverhang: input.leftOverhang } : {}),
        ...(input.rightOverhang != null ? { rightOverhang: input.rightOverhang } : {}),
        ...(input.plies != null ? { numberOfPlies: input.plies } : {}),
        ...(input.type != null ? { componentType: input.type } : {}),
      };
      if (Object.keys(editedProperties).length === 0) throw new ToolError('Give something to change.');

      await dispatch(editExistingTrussEnvelopes(envelopes.map(({ guid }) => ({ guid, editedProperties }))));
      // Re-fitting would put back the overhangs the roof gives, so only re-fit when the ends or type changed.
      const refit = (input.recalculate ?? true) && input.leftOverhang == null && input.rightOverhang == null;
      if (refit)
        await dispatch(
          recalculateExistingTrussEnvelopes(envelopes.map(({ guid }) => getState().layout.trussEnvelopes[guid])),
        );
      await waitForAppToSettle(getState);

      return {
        summary: {
          edited: envelopes.map(({ guid }) => {
            const saved = getState().layout.trussEnvelopes[guid];
            return {
              name: saved.name,
              from: saved.leftPoint,
              to: saved.rightPoint,
              leftOverhang: saved.leftOverhang,
              rightOverhang: saved.rightOverhang,
              plies: saved.numberOfPlies,
            };
          }),
        },
      };
    },
  }),

  defineTool<{ envelopes?: string[]; walls?: string[]; from: unknown; to: unknown }>({
    name: 'trim_or_extend',
    description:
      'Trims or extends truss envelopes and walls to a line through two plan points (inches), as Trim/Extend does: ' +
      'each one’s nearer end moves to where it crosses the line. Use it to stop jacks or floor trusses at a beam.',
    inputSchema: {
      type: 'object',
      properties: {
        envelopes: { type: 'array', items: { type: 'string' }, description: 'Truss envelope names.' },
        walls: { type: 'array', items: { type: 'string' }, description: 'Wall or beam names.' },
        from: POINT_SCHEMA,
        to: POINT_SCHEMA,
      },
      required: ['from', 'to'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      const trussEnvelopes = (input.envelopes ?? []).map((name) => findTrussEnvelopeByName(state, name));
      const bearingEnvelopes = (input.walls ?? []).map((name) => findBearingEnvelopeByName(state, name));
      if (trussEnvelopes.length + bearingEnvelopes.length === 0)
        throw new ToolError('Name envelopes or walls to trim.');

      const from = requirePoint(input.from, 'from');
      const to = requirePoint(input.to, 'to');
      if (from.x === to.x && from.y === to.y) throw new ToolError('The line needs two different points.');

      await dispatch(
        trimOrExtendEnvelopes(
          {
            trussEnvelopeGuids: trussEnvelopes.map(({ guid }) => guid),
            bearingEnvelopeGuids: bearingEnvelopes.map(({ guid }) => guid),
          },
          Line2D.fromPointObjects(from, to),
        ),
      );
      await waitForAppToSettle(getState);

      const after = getState();
      return {
        summary: {
          envelopes: trussEnvelopes.map(({ guid }) => ({
            name: after.layout.trussEnvelopes[guid].name,
            from: after.layout.trussEnvelopes[guid].leftPoint,
            to: after.layout.trussEnvelopes[guid].rightPoint,
          })),
          walls: bearingEnvelopes.map(({ guid }) => ({
            name: after.layout.bearingEnvelopes[guid].name,
            from: after.layout.bearingEnvelopes[guid].leftPoint,
            to: after.layout.bearingEnvelopes[guid].rightPoint,
          })),
        },
      };
    },
  }),

  defineTool<{ trusses: string[] }>({
    name: 'delete_trusses',
    description: 'Deletes trusses by name. Their truss envelopes stay, empty. It can’t be undone.',
    inputSchema: {
      type: 'object',
      properties: { trusses: { type: 'array', items: { type: 'string' } } },
      required: ['trusses'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesigns = requireStringArray(input, 'trusses').map((name) => findTrussByName(getState(), name));
      await dispatch(deleteComponentDesignsWithoutConfirmation(componentDesigns.map(({ guid }) => guid)));
      await waitForAppToSettle(getState);

      const remaining = componentDesigns.filter(({ guid }) => getState().componentDesigns.data[guid] != null);
      if (remaining.length > 0)
        throw new ToolError(`Could not delete ${remaining.map(({ component }) => component.name).join(', ')}.`);
      return { summary: { deleted: componentDesigns.map(({ component }) => component.name) } };
    },
  }),

  defineTool<{ envelopes: string[] }>({
    name: 'delete_truss_envelopes',
    description: 'Deletes truss envelopes by name. A truss in one is deleted with it.',
    inputSchema: {
      type: 'object',
      properties: { envelopes: { type: 'array', items: { type: 'string' } } },
      required: ['envelopes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const envelopes = requireStringArray(input, 'envelopes').map((name) => findTrussEnvelopeByName(getState(), name));
      await dispatch(
        deleteItems(envelopes.map((envelope) => ({ type: 'trussEnvelope' as const, guid: envelope.guid }))),
      );
      await waitForAppToSettle(getState);

      const remaining = envelopes.filter((envelope) => getState().layout.trussEnvelopes[envelope.guid] != null);
      if (remaining.length > 0)
        throw new ToolError(`Could not delete ${remaining.map((envelope) => envelope.name).join(', ')}.`);
      return { summary: { deleted: envelopes.map((envelope) => envelope.name) } };
    },
  }),
];
